import React, { useState, useEffect } from "react";
import { 
  Users, 
  Search, 
  Trash2, 
  ShieldCheck, 
  MoreVertical,
  Activity,
  AlertCircle,
  Building2,
  Medal,
  Award
} from "lucide-react";
import AdminSidebar from "./AdminSidebar";
import { db } from "../../firebase/config";
import { useToast } from "../../context/ToastContext";
import { ref, onValue, remove, update } from "firebase/database";
import "./admin.css";

const ManageDoctors = () => {
  const { showAlert } = useToast();
  const [doctors, setDoctors] = useState([]);
  const [hospitals, setHospitals] = useState({});
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    // Fetch Hospitals for identity resolution
    const hospitalsRef = ref(db, "hospitals");
    onValue(hospitalsRef, (snapshot) => {
      if (snapshot.exists()) setHospitals(snapshot.val());
    });

    // Fetch Doctors
    const usersRef = ref(db, "users");
    const unsubscribe = onValue(usersRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const list = Object.entries(data)
          .filter(([uid, val]) => val.role === "doctor")
          .map(([uid, val]) => ({ uid, ...val }));
        setDoctors(list);
      } else {
        setDoctors([]);
      }
    });

    return () => unsubscribe();
  }, []);

  const resolveHospitalName = (id) => {
    return hospitals[id]?.name || id || "INDEPENDENT_NODE";
  };

  const handleStatusUpdate = async (uid, status) => {
    try {
      await update(ref(db, `users/${uid}`), { status });
      showAlert(`PROTOCOL_UPDATED: Practitioner node status changed to ${status.toUpperCase()}.`, "info");
    } catch (error) {
      showAlert("STATUS_SYNC_FAILURE: " + error.message, "error");
    }
  };

  const handleDeleteDoctor = async (uid) => {
    if (window.confirm("CONFIRM_ACTION: Revoke all clinical access for this identity node?")) {
      try {
        await remove(ref(db, `users/${uid}`));
        showAlert("IDENTITY_REVOKED: Staff record purged.", "success");
      } catch (error) {
        showAlert("PROTOCOL_ERROR: " + error.message, "error");
      }
    }
  };

  const filteredDoctors = doctors.filter(doc => 
    doc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    doc.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (doc.license && doc.license.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="admin-shell">
      <AdminSidebar />
      
      <main className="admin-content">
        <header className="admin-header">
          <div className="header-info">
            <h1>STAFF_REGISTRY</h1>
            <p>OVERSIGHT_NODE: SECURE_PORT_v2.0</p>
          </div>
          <div className="system-status">
            <ShieldCheck size={14} className="icon-blue pulse" strokeWidth={3} />
            <span>REGISTRY: AUTHENTICATED</span>
          </div>
        </header>

        <section className="registry-controls glass-card" style={{ marginBottom: '2rem', padding: '2rem' }}>
          <div className="search-group" style={{ maxWidth: 'unset' }}>
            <Search size={18} className="icon-blue" strokeWidth={2.5} />
            <input 
              type="text" 
              placeholder="FILTER_BY: NAME, EMAIL, OR_LICENSE_ID..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.8rem', letterSpacing: '1px' }}
            />
          </div>
          <div className="registry-summary" style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.7rem', color: 'var(--admin-accent-blue)' }}>
            ACTIVE_NODES: [ {doctors.length.toString().padStart(3, '0')} ]
          </div>
        </section>

        <div className="registry-list-section glass-card">
          <table className="registry-table">
            <thead>
              <tr>
                <th>IDENTITY_NODE</th>
                <th>SPECIALTY / LICENSE</th>
                <th>EXP_METRIC</th>
                <th>ASSOCIATED_FACILITY</th>
                <th>STATUS</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filteredDoctors.length > 0 ? (
                filteredDoctors.map((doc) => (
                  <tr key={doc.uid}>
                    <td>
                      <div className="table-user-info">
                        <div className="user-avatar">{doc.name.charAt(0)}</div>
                        <div className="user-text">
                          <span className="user-name" style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.85rem' }}>{doc.name}</span>
                          <span className="user-email" style={{ fontSize: '0.7rem', color: 'var(--admin-accent-blue)', opacity: 0.7 }}>{doc.email}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="credential-stack">
                        <span className="spec-label" style={{ fontSize: '0.75rem', textTransform: 'uppercase' }}>{doc.specialty || "GEN_PHYSICIAN"}</span>
                        <span className="license-badge" style={{ fontFamily: 'Space Mono, monospace', color: 'var(--admin-accent-cyan)' }}>
                          <Medal size={10} /> {doc.license || "MC-PENDING"}
                        </span>
                      </div>
                    </td>
                    <td>
                      <span className="exp-text" style={{ fontFamily: 'Space Mono, monospace' }}>
                        {doc.experience ? `${doc.experience.toString().padStart(2, '0')}_YRS` : "00_YRS"}
                      </span>
                    </td>
                    <td>
                      <div className="hospital-link">
                        <Building2 size={12} className="icon-blue" />
                        <span style={{ fontSize: '0.75rem' }}>{resolveHospitalName(doc.hospitalId)}</span>
                      </div>
                    </td>
                    <td>
                      <span className={`status-badge ${doc.status === 'verified' ? 'active' : 'pending'}`}>
                        {doc.status || "WAITING"}
                      </span>
                    </td>
                    <td>
                      <div className="table-actions">
                        {doc.status !== "verified" && (
                          <button 
                            className="icon-btn verify" 
                            onClick={() => handleStatusUpdate(doc.uid, "verified")}
                            title="AUTHORIZE_PRACTITIONER"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--admin-success)' }}
                          >
                            <ShieldCheck size={16} strokeWidth={2.5} />
                          </button>
                        )}
                        {doc.status !== "rejected" && doc.status !== "verified" && (
                          <button 
                            className="icon-btn reject" 
                            onClick={() => handleStatusUpdate(doc.uid, "rejected")}
                            title="DE_AUTHORIZE_PRACTITIONER"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--admin-accent-red)' }}
                          >
                            <AlertCircle size={16} strokeWidth={2.5} />
                          </button>
                        )}
                        <button className="icon-btn delete" onClick={() => handleDeleteDoctor(doc.uid)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--admin-accent-red)', opacity: 0.6 }}>
                          <Trash2 size={16} strokeWidth={2.5} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="empty-registry" style={{ padding: '5rem', textAlign: 'center' }}>
                    <Activity size={48} className="icon-dim pulse" />
                    <p style={{ marginTop: '1.5rem', fontFamily: 'Space Mono, monospace', fontSize: '0.8rem', color: 'var(--admin-text-dim)' }}>
                      NO_IDENTITY_NODES_FOUND_MATCHING_SEARCH_PARAM
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
};

export default ManageDoctors;
